import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import beforeAfterImage from "../../../assets/before_after.png";
import diversModels from "../../../assets/9_images_of_diverse_models.jpg";
import imageSavingTime from "../../../assets/image_saving_time.jpg";
import creativity from "../../../assets/creativity.png";

const DemoVideoBlock = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setPlaying(!playing);
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="relative w-full rounded-xl overflow-hidden shadow-xl bg-black"
      style={{ height: '400px' }}
    >
      <video
        ref={videoRef}
        src="/gomens-demo.mp4"
        poster="/gomens-thumb.jpg"
        className="w-full h-full object-cover"
        loop
        playsInline
        muted
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        onClick={togglePlay}
        className="absolute inset-0 flex items-center justify-center group cursor-pointer"
        aria-label={playing ? 'Pause video' : 'Play video'}
      >
        <div className={`w-14 h-14 rounded-full bg-white bg-opacity-90 flex items-center justify-center shadow-lg transition-opacity duration-300 ${
          playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        }`}>
          {playing ? <Pause className="w-5 h-5 text-gray-900" /> : <Play className="w-5 h-5 text-gray-900 ml-1" />}
        </div>
      </button>
    </motion.div>
  );
};

export const FeatureSection = () => {
  return (
    <>
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col-reverse lg:flex-row-reverse gap-10 items-center md:gap-12 mb-10"
            >
              <div className="lg:w-1/2">
                <div className="relative ">
                  {/* <ReactCompareImage 
                     aspectRatio='wider'
                     leftImage={beforeImage} 
                     rightImage={afterImage} 
                  />; */}
                  {/* { <ReactBeforeSliderComponent
                      firstImage={beforeImage}
                      secondImage={afterImage}
                  /> } */}
                  <img
                    src={beforeAfterImage}
                    alt="before & after image"
                    className="w-full max-w-[550px] h-auto object-cover rounded-md shadow-sm"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <h3 className="text-[45px] font-[Playfair-Display] font-bold mb-4 ">
                  Skip the Slow Photoshoots
                </h3>
                <div>
                  <div>
                    <h4 className="text-[20px] font-bold ">Endless Costs:</h4>
                    <p className="text-[17px]">
                      Studios, models, stylists, photographers... the bills add up.
                    </p><br/>
                  </div>
                </div>
                <h4 className="text-[20px] font-bold ">Wasted Time:</h4>
                <p className="text-[17px]">
                  Weeks of planning and shooting for images you use once.
                </p><br/>
                <h4 className="text-[20px] font-bold ">Creative Limits:</h4>
                <p className="text-[17px]">
                 One model, one location, one look. Limited diversity and flexibility.
                </p>                
              </div>
            </motion.div>
            {/* Demo Video — From Garment to Campaign in Seconds */}
            <div className="mt-1 mb-0">
              <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-[Playfair-Display] font-bold italic mb-6 text-italics">
                From Garment to Campaign in Seconds
              </h3>
            </div>
            <DemoVideoBlock />

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col-reverse lg:flex-row-reverse items-center -mt-24 md:mt-0 md:gap-12"
            >
              <div className="lg:w-1/2">
                <div className="relative">
                  <img
                    src={diversModels}
                    alt="diverse models"
                    className="w-[450px] h-[420px]"
                  />
                </div>
              </div>
               <div className="lg:w-1/2">
                <h3 className="text-[45px] font-bold mb-1 mt-4">
                 Unmatched Inclusivity
                </h3>
                <div>
                  <div>
                    <p className="text-[17px] mb-4">
                      Reflect your true audience. Generate models of any ethnicity, age, body type, and size to build authentic connections.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col-reverse lg:flex-row items-center -mt-24 md:mt-0 md:gap-12"
            >
              <div className="lg:w-1/2">
                <div className="relative">
                  <img
                    src={imageSavingTime}
                    alt="image saving time and money"
                    className="w-[550px] h-[450px]"
                  />
                </div>
              </div>
               <div className="lg:w-1/2">
                <h3 className="text-[45px] font-bold mb-1 mt-5">
                 Radical Speed & Savings
                </h3>
                <p className="text-[17px] mb-4">
                Launch new drops the same day you design them. Eliminate 80% of your traditional photoshoot costs and reinvest in growth.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col-reverse lg:flex-row-reverse items-center -mt-24 md:mt-0 md:gap-12"
            >
              <div className="lg:w-1/2">
                <div className="relative">
                  <img
                    src={creativity}
                    alt="Image creativity"
                    className="w-[550px] h-[320px]"
                  />
                </div>
              </div>
               <div className="lg:w-1/2">
                <h3 className="text-[45px] font-bold mb-1">
                 Limitless Creativity
                </h3>                              
                <p className="text-[17px]">
                  Your vision, unconstrained. From minimalist studio shots to exotic location campaigns, you are the director of a photoshoot with infinite options.
                </p>                             
              </div>
            </motion.div>           
          </div>
        </div>
      </section>
    </>
  );
};
